import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { EmptyState, Page, PageHeader, TableSkeleton } from "../../../components/ui";
import {
  QUILL_FORMATS,
  QUILL_MODULES,
} from "../../../components/editor/quillConfig";
import { stripHtml } from "../../../utils/list";
import SectionCard from "./SectionCard";
import "../ads/ad-form.css";

/** True when an editor value has no text (e.g. "<p><br></p>"). */
export const isBlank = (html) => !stripHtml(html).trim();

/**
 * Page holding one rich text of the apps (terms, privacy policy…).
 *
 * - `load()`: resolves with the settings, the text being `data[field]`
 * - `save({ data })`: resolves true once saved
 * - `texts`: `{ title, empty, saved, failed }` translation keys
 */
const LegalTextPage = ({ load, field, save, texts }) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = () =>
    load().then((data) => {
      const value = data?.[field] || "";
      setText(value);
      setDraft(value);
      return value;
    });

  useEffect(() => {
    refresh().then((value) => {
      // Nothing written yet: start with the editor.
      setEditing(!value);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (isBlank(draft)) {
      toast.error(t(texts.empty));
      return;
    }
    setSaving(true);
    const saved = await save({ data: draft }).catch(() => false);
    setSaving(false);
    if (!saved) {
      toast.error(t(texts.failed));
      return;
    }
    await refresh();
    setEditing(false);
    toast.success(t(texts.saved));
  };

  return (
    <Page>
      <PageHeader
        title={t(texts.title)}
        actions={
          !loading &&
          !editing && (
            <button
              type="button"
              className="ui-btn ui-btn-secondary"
              onClick={() => {
                setDraft(text);
                setEditing(true);
              }}
            >
              <i className="ri-pencil-line" aria-hidden="true"></i>
              {t("edit")}
            </button>
          )
        }
      />
      {loading ? (
        <section className="ui-card">
          <TableSkeleton rows={6} />
        </section>
      ) : (
        <SectionCard
          editing={editing}
          saving={saving}
          onCancel={text ? () => setEditing(false) : undefined}
          onSave={handleSave}
        >
          {editing ? (
            <ReactQuill
              className="ui-editor legal-editor"
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              value={draft}
              onChange={setDraft}
            />
          ) : text ? (
            <div className="legal-html" dangerouslySetInnerHTML={{ __html: text }} />
          ) : (
            <EmptyState icon="ri-file-text-line">{t("Not filled yet")}</EmptyState>
          )}
        </SectionCard>
      )}
    </Page>
  );
};

export default LegalTextPage;
